import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, retry, throwError, timer } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AlertService } from '../services/alert.service';
import { ConfigService } from '../services/config.service';
import { LoggingService } from '../services/logging.service';
import { NotificationService } from '../services/notification.service';
import { ConfigClientService } from '../services';
import { CONTEXT } from '../models/context';

const MAX_RETRIES = 2;
const RATE_LIMIT_STATUS = 429;
const RETRY_BASE_DELAY_MS = 1000;

type ApiErrorType = 'ApiSinError' | 'ApiIntegraError' | 'ApiGpoError' | '';

type ApiSinPayload = {
  Succeeded?: boolean;
  Errors?: string[];
  Message?: string;
};

type ApiIntegraPayload = {
  succeeded?: boolean;
  errors?: string[];
  message?: string;
};

const hasKeys = (value: unknown, keys: string[]): boolean => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));
};

const isApiSinError = (value: unknown): value is ApiSinPayload =>
  hasKeys(value, ['Succeeded', 'Errors', 'Message']);

const isApiIntegraError = (value: unknown): value is ApiIntegraPayload =>
  hasKeys(value, ['succeeded', 'errors', 'message']);

const isApiGpoError = (value: unknown): value is ApiIntegraPayload =>
  hasKeys(value, ['succeeded', 'errors', 'message']);

const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const normalizePath = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return '/';
  }

  return `/${trimmed.replace(/^\/+/, '')}`.replace(/\/+$/, '');
};

const isApiRequest = (requestUrl: string, apiBaseUrl: string): boolean => {
  if (!apiBaseUrl) {
    return false;
  }

  if (isAbsoluteUrl(apiBaseUrl)) {
    return requestUrl.startsWith(apiBaseUrl);
  }

  if (isAbsoluteUrl(requestUrl)) {
    const url = new URL(requestUrl);
    return normalizePath(url.pathname).startsWith(normalizePath(apiBaseUrl));
  }

  return normalizePath(requestUrl).startsWith(normalizePath(apiBaseUrl));
};

const resolveApiErrorType = (
  error: HttpErrorResponse,
  configClientService: ConfigClientService,
): ApiErrorType => {
  if (!error.url) {
    return '';
  }

  const payload = error.error;
  const matchesSin =
    isApiRequest(error.url, configClientService.getConfig()?.api_sinergial ?? '') &&
    isApiSinError(payload);
  if (matchesSin) {
    return 'ApiSinError';
  }

  const matchesIntegra =
    isApiRequest(error.url, configClientService.getConfig()?.api_integra ?? '') &&
    isApiIntegraError(payload);
  if (matchesIntegra) {
    return 'ApiIntegraError';
  }

  if (isApiGpoError(payload)) {
    return 'ApiGpoError';
  }

  return '';
};

const extractApiMessage = (payload: unknown): string | null => {
  if (typeof payload === 'string') {
    return payload;
  }

  if (isApiSinError(payload)) {
    return payload.Errors?.[0] ?? payload.Message ?? null;
  }

  if (isApiIntegraError(payload) || isApiGpoError(payload)) {
    return payload.message ?? payload.errors?.[0] ?? null;
  }

  if (payload && typeof payload === 'object' && 'message' in payload) {
    const messageValue = (payload as { message?: unknown }).message;
    return typeof messageValue === 'string' ? messageValue : null;
  }

  return null;
};

const resolveUserMessage = (status: number, fallbackMessage: string | null): string => {
  if (!environment.production && fallbackMessage) {
    return fallbackMessage;
  }

  switch (status) {
    case 0:
      return 'No se pudo conectar con el servicio. Revisa tu conexion.';
    case 400:
      return 'La solicitud no es valida. Verifica los datos ingresados.';
    case 401:
      return 'Tu sesion expiro. Inicia sesion nuevamente.';
    case 403:
      return 'No tienes permisos para realizar esta accion.';
    case 404:
      return 'No encontramos el recurso solicitado.';
    case 429:
      return 'Demasiadas solicitudes. Intenta nuevamente en unos segundos.';
    case 500:
      return 'Servicio no disponible. Intenta mas tarde.';
    default:
      return 'Ocurrio un error inesperado. Intenta nuevamente.';
  }
};

const shouldUseCriticalAlert = (status: number): boolean => status === 0 || status === 500;

const resolveCriticalTitle = (status: number): string =>
  status === 0 ? 'Sin conexion' : 'Servicio no disponible';

const notifyUser = (
  notificationService: NotificationService,
  status: number,
  message: string,
): void => {
  if ([401, 403, 429].includes(status)) {
    notificationService.warn(message);
    return;
  }

  notificationService.error(message);
};

export const errorInterceptor: HttpInterceptorFn = (request, next) => {
  const context = request.context.get(CONTEXT);
  const configClientService = inject(ConfigClientService);
  const notificationService = inject(NotificationService);
  const alertService = inject(AlertService);
  const loggingService = inject(LoggingService);
  let rateLimitNotified = false;

  return next(request).pipe(
    retry({
      count: MAX_RETRIES,
      delay: (error, retryCount) => {
        if (error instanceof HttpErrorResponse && error.status === RATE_LIMIT_STATUS) {
          if (!rateLimitNotified) {
            notificationService.warn('Demasiadas solicitudes. Reintentando...');
            rateLimitNotified = true;
          }

          return timer(RETRY_BASE_DELAY_MS * retryCount);
        }

        return throwError(() => error);
      },
    }),
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        loggingService.logHttpError(error, {
          url: request.url,
          method: request.method,
        });
        notificationService.error('Ocurrio un error inesperado. Intenta nuevamente.');
        return throwError(() => error);
      }

      const apiType = resolveApiErrorType(error, configClientService);
      const apiMessage = extractApiMessage(error.error);
      const message = resolveUserMessage(error.status, apiMessage);

      loggingService.logHttpError(error, {
        url: error.url ?? request.url,
        method: request.method,
        status: error.status,
        apiType,
      });

      if (!context.ignoreError) {
        if (shouldUseCriticalAlert(error.status)) {
          void alertService.error(message, {
            title: resolveCriticalTitle(error.status),
            suppressToasts: true,
          });
        } else {
          notifyUser(notificationService, error.status, message);
        }
      }

      return throwError(() => error);
    }),
  );
};
